import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/dates";
import { StatusPill } from "@/components/Layout";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import TechMultiSelect from "@/components/TechMultiSelect";
import { useCalendarEvents, classifyDate } from "@/lib/calendarEvents";
import { Plus, Search, Trash2, Pencil, FileClock, AlertTriangle, X, Link2 } from "lucide-react";
import { toast } from "sonner";

const CUSTOMER_TYPES = ["Builder", "Commercial Client", "Residential Client", "Body Corporate", "Government", "Other"];
const empty = {
  builder: "", customer_id: "", customer_name: "", customer_type: "", site_address: "", contact_person: "",
  contact_position: "", contact_email: "", contact_phone: "",
  suburb: "", state: "", postcode: "", country: "", latitude: null, longitude: null,
  project_name: "",
  phone: "", appointment_date: new Date().toISOString().slice(0, 10), start_time: "", finish_time: "",
  technician_id: "", technician_ids: [], scope_of_works: "", internal_notes: "",
  billing_cycle: "weekly",
};

function ScheduleDaysEditor({ days, onChange, techs, events, excludeJobId }) {
  const [conflicts, setConflicts] = useState([]);
  const today = new Date().toISOString().slice(0, 10);
  useEffect(() => {
    const payloadDays = days.filter((d) => d.date && (d.worker_ids || []).length).map((d) => ({ date: d.date, worker_ids: d.worker_ids }));
    if (!payloadDays.length) { setConflicts([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/jobs/check-conflicts", { days: payloadDays, exclude_job_id: excludeJobId || null });
        setConflicts(r.data.conflicts || []);
      } catch { setConflicts([]); }
    }, 500);
    return () => clearTimeout(t);
  }, [JSON.stringify(days), excludeJobId]);

  const upd = (i, patch) => onChange(days.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  const remove = (i) => onChange(days.filter((_, idx) => idx !== i));
  const add = () => {
    const last = days[days.length - 1];
    let nd = today;
    if (last?.date) { const dt = new Date(last.date); dt.setDate(dt.getDate() + 1); nd = dt.toISOString().slice(0, 10); }
    onChange([...days, { date: nd, start_time: last?.start_time || "", finish_time: last?.finish_time || "", worker_ids: [] }]);
  };
  const dayConflicts = (d) => conflicts.filter((c) => c.date === d.date && (d.worker_ids || []).includes(c.worker_id));

  return (
    <div className="sm:col-span-2 space-y-3" data-testid="schedule-days-editor">
      {days.map((d, i) => {
        const di = classifyDate(events, d.date);
        const cf = dayConflicts(d);
        return (
          <div key={i} data-testid={`schedule-day-${i}`} className="border border-slate-200 rounded-xl p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-500">Day {i + 1}</span>
              {days.length > 1 && <button type="button" data-testid={`schedule-day-remove-${i}`} onClick={() => remove(i)} className="text-red-500 p-1"><X className="w-4 h-4" /></button>}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="col-span-2"><Label className="text-xs">Date</Label><Input data-testid={`schedule-day-date-${i}`} type="date" value={d.date} onChange={(e) => upd(i, { date: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">Start</Label><Input type="time" value={d.start_time} onChange={(e) => upd(i, { start_time: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">Finish</Label><Input type="time" value={d.finish_time} onChange={(e) => upd(i, { finish_time: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="mt-2"><Label className="text-xs">Assigned to this day</Label>
              <TechMultiSelect techs={techs} value={d.worker_ids} onChange={(v) => upd(i, { worker_ids: v })} testid={`schedule-day-techs-${i}`} /></div>
            {(di.holiday || di.rdo || cf.length > 0) && (
              <div data-testid={`schedule-day-warning-${i}`} className="mt-2 text-xs rounded-lg bg-amber-50 border border-amber-200 p-2 text-amber-800 space-y-0.5">
                {di.holiday && <p>⚠ Public Holiday: {di.holiday}</p>}
                {di.rdo && <p>⚠ Construction RDO — sites may be closed</p>}
                {cf.map((c, k) => <p key={k}>⚠ {c.worker_name}: {c.label}</p>)}
              </div>
            )}
          </div>
        );
      })}
      <Button type="button" variant="outline" size="sm" data-testid="schedule-day-add" onClick={add}><Plus className="w-4 h-4 mr-1" />Add another day</Button>
    </div>
  );
}


export function JobForm({ open, setOpen, job, customers, techs, onSaved, reloadCustomers, initialCustomer }) {
  const [form, setForm] = useState(empty);
  const [multiDay, setMultiDay] = useState(false);
  const toggleMultiDay = (on) => {
    if (on) {
      const seed = (form.schedule_days && form.schedule_days.length) ? form.schedule_days
        : [{ date: form.appointment_date || new Date().toISOString().slice(0, 10), start_time: form.start_time || "", finish_time: form.finish_time || "", worker_ids: form.technician_ids || [] }];
      setForm((f) => ({ ...f, schedule_days: seed }));
    } else {
      const d0 = (form.schedule_days || [])[0];
      setForm((f) => ({ ...f, schedule_days: null,
        appointment_date: d0?.date || f.appointment_date, start_time: d0?.start_time ?? f.start_time,
        finish_time: d0?.finish_time ?? f.finish_time, technician_ids: d0?.worker_ids || f.technician_ids }));
    }
    setMultiDay(on);
  };
  const [newCustOpen, setNewCustOpen] = useState(false);
  const [savingCust, setSavingCust] = useState(false);
  const emptyCust = { name: "", customer_type: "Builder", builder: "", phone: "", email: "" };
  const [newCust, setNewCust] = useState(emptyCust);
  const [prevAddresses, setPrevAddresses] = useState([]);
  const [prevContacts, setPrevContacts] = useState([]);

  const loadPrevAddresses = async (id) => {
    setPrevAddresses([]); setPrevContacts([]);
    if (!id) return;
    const cust = customers.find((x) => x.id === id);
    const contacts = []; const seenC = new Set();
    const pushC = (name, title, phone, email) => {
      const nm = (name || "").trim(); if (!nm) return;
      const key = nm.toLowerCase() + "|" + (phone || "").trim();
      if (seenC.has(key)) return;
      seenC.add(key); contacts.push({ name: nm, title: title || "", phone: phone || "", email: email || "" });
    };
    (cust?.contacts || []).forEach((c) => pushC(c.name, c.position, c.mobile, c.email));
    try {
      const r = await api.get(`/customers/${id}/history`);
      const jobs = [...(r.data.active_jobs || []), ...(r.data.awaiting_invoices || []), ...(r.data.previous_jobs || [])];
      const seen = new Set(); const out = [];
      jobs.forEach((j) => {
        const a = (j.site_address || "").trim();
        if (a && !seen.has(a)) { seen.add(a); out.push({ address: a, suburb: j.suburb, state: j.state, postcode: j.postcode, latitude: j.latitude, longitude: j.longitude }); }
        pushC(j.contact_person, j.contact_position, j.contact_phone || j.phone, j.contact_email);
      });
      setPrevAddresses(out);
    } catch (e) { /* no history yet */ }
    setPrevContacts(contacts);
  };

  useEffect(() => {
    if (job) {
      const ids = job.technician_ids && job.technician_ids.length ? job.technician_ids : (job.technician_id ? [job.technician_id] : []);
      setForm({ ...empty, ...job, technician_ids: ids });
      setMultiDay(!!(job.schedule_days && job.schedule_days.length));
      loadPrevAddresses(job.customer_id);
    } else if (initialCustomer) {
      setMultiDay(false);
      setForm({ ...empty, customer_id: initialCustomer.id, customer_name: initialCustomer.name,
        customer_type: initialCustomer.customer_type || "", builder: initialCustomer.name, phone: initialCustomer.phone || "" });
      loadPrevAddresses(initialCustomer.id);
    } else { setMultiDay(false); setForm(empty); setPrevAddresses([]); setPrevContacts([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job, open, initialCustomer]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const createCustomer = async () => {
    if (!newCust.name.trim()) return;
    setSavingCust(true);
    try {
      const r = await api.post("/customers", { ...newCust, builder: newCust.builder || newCust.name });
      const list = reloadCustomers ? await reloadCustomers() : customers;
      const created = list.find((x) => x.id === r.data.id) || r.data;
      onCustomer(created.id);
      toast.success("Customer created & selected");
      setNewCustOpen(false); setNewCust(emptyCust);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingCust(false); }
  };

  const onCustomer = (id) => {
    const c = customers.find((x) => x.id === id);
    setForm((f) => ({ ...f, customer_id: id, customer_name: c?.name || f.customer_name,
      builder: c?.builder || c?.name || f.builder, customer_type: c?.customer_type || "",
      site_address: "", suburb: "", state: "", postcode: "", country: "", latitude: null, longitude: null,
      phone: c?.phone || f.phone, contact_person: "", contact_position: "", contact_email: "", contact_phone: "" }));
    loadPrevAddresses(id);
  };
  const events = useCalendarEvents();
  const [conflict, setConflict] = useState(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [holidayReason, setHolidayReason] = useState("");
  const [rdoAck, setRdoAck] = useState("");
  const dateInfo = classifyDate(events, form.appointment_date);
  const isHoliday = !!dateInfo.holiday;
  const showRdoPopup = !!dateInfo.rdo && rdoAck !== form.appointment_date;

  const save = async (override = false) => {
    try {
      const payload = { ...form, technician_ids: form.technician_ids || [], customer_id: form.customer_id || null };
      if (override) { payload.override_availability = true; payload.override_reason = overrideReason; }
      if (isHoliday) {
        if (!holidayReason.trim()) { toast.error("Enter a reason to schedule work on a public holiday."); return; }
        payload.override_holiday = true;
        payload.holiday_override_reason = holidayReason.trim();
      }
      if (job) await api.put(`/jobs/${job.id}`, payload);
      else await api.post("/jobs", payload);
      toast.success(job ? "Job updated" : "Job created");
      setConflict(null); setOverrideReason(""); setHolidayReason(""); setOpen(false); onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.error === "scheduling_conflict") {
        setConflict(detail);
      } else if (e.response?.status === 409 && detail?.error === "public_holiday") {
        toast.error("This date is a public holiday — enter a reason below to override.");
      } else {
        toast.error(formatApiError(detail));
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{job ? "Edit Job" : "New Job"}</DialogTitle></DialogHeader>
        {isHoliday && (
          <div data-testid="holiday-block" className="rounded-xl border border-red-200 bg-red-50 p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-700 mt-0.5 shrink-0" />
              <div className="text-sm w-full">
                <span className="font-semibold text-red-900">Public Holiday — booking blocked</span>
                <p className="text-red-800 text-xs mt-0.5">{dateInfo.holiday}. Work isn't normally scheduled on public holidays. Choose another date, or enter a reason to override (recorded in the audit log).</p>
                <Input data-testid="holiday-override-reason" value={holidayReason} onChange={(e) => setHolidayReason(e.target.value)}
                  placeholder="Reason to schedule on this public holiday" className="mt-2 bg-white" />
                <div className="flex gap-2 justify-end mt-2">
                  <Button type="button" variant="outline" size="sm" data-testid="holiday-select-another" onClick={() => { set("appointment_date", ""); setHolidayReason(""); }}>Select Another Date</Button>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <div className="flex items-center justify-between">
              <Label>Customer / Builder</Label>
              <button type="button" data-testid="job-new-customer-toggle" onClick={() => setNewCustOpen((o) => !o)}
                className="text-xs text-blue-700 font-medium flex items-center gap-1"><Plus className="w-3.5 h-3.5" />New Customer</button>
            </div>
            <Select value={form.customer_id || undefined} onValueChange={onCustomer}>
              <SelectTrigger data-testid="job-customer-select" className="mt-1"><SelectValue placeholder="— Select customer / builder —" /></SelectTrigger>
              <SelectContent>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} {c.customer_type ? `· ${c.customer_type}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
            {newCustOpen && (
              <div data-testid="job-new-customer-panel" className="mt-2 border border-blue-200 bg-blue-50/50 rounded-lg p-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">Name *</Label><Input data-testid="new-cust-name" value={newCust.name} onChange={(e) => setNewCust((c) => ({ ...c, name: e.target.value }))} className="mt-1 bg-white" /></div>
                  <div><Label className="text-xs">Type</Label>
                    <Select value={newCust.customer_type} onValueChange={(v) => setNewCust((c) => ({ ...c, customer_type: v }))}>
                      <SelectTrigger data-testid="new-cust-type" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>{CUSTOMER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Phone</Label><Input data-testid="new-cust-phone" value={newCust.phone} onChange={(e) => setNewCust((c) => ({ ...c, phone: e.target.value }))} className="mt-1 bg-white" /></div>
                  <div><Label className="text-xs">Email</Label><Input data-testid="new-cust-email" value={newCust.email} onChange={(e) => setNewCust((c) => ({ ...c, email: e.target.value }))} className="mt-1 bg-white" /></div>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" size="sm" onClick={() => setNewCustOpen(false)}>Cancel</Button>
                  <Button type="button" data-testid="new-cust-save" size="sm" onClick={createCustomer} disabled={savingCust || !newCust.name.trim()} className="bg-navy hover:bg-navy-light">{savingCust ? "Creating…" : "Create & Select"}</Button>
                </div>
              </div>
            )}
          </div>
          <div><Label>Builder / Company</Label><Input data-testid="job-builder-input" value={form.builder} onChange={(e) => set("builder", e.target.value)} className="mt-1" /></div>
          <div><Label>Project / Site (optional)</Label><Input data-testid="job-project-input" value={form.project_name} onChange={(e) => set("project_name", e.target.value)} className="mt-1" placeholder="e.g. Riverside Apartments" /></div>
          <div className="sm:col-span-2">
            <Label>Site Contact / Foreman</Label>
            {prevContacts.length > 0 && (
              <Select value={undefined} onValueChange={(v) => {
                const c = prevContacts[Number(v)];
                if (c) setForm((f) => ({ ...f, contact_person: c.name, contact_position: c.title, contact_phone: c.phone, contact_email: c.email }));
              }}>
                <SelectTrigger data-testid="prev-contact-select" className="mt-1 mb-2"><SelectValue placeholder="Use a previous site contact…" /></SelectTrigger>
                <SelectContent>
                  {prevContacts.map((c, i) => (
                    <SelectItem key={i} value={String(i)}>{c.name}{c.title ? ` · ${c.title}` : ""}{c.phone ? ` · ${c.phone}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
              <Input data-testid="job-contact-name" value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} placeholder="Contact name" />
              <Input data-testid="job-contact-title" value={form.contact_position} onChange={(e) => set("contact_position", e.target.value)} placeholder="Title / Position" />
              <Input data-testid="job-contact-phone" value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} placeholder="Contact phone" />
              <Input data-testid="job-contact-email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} placeholder="Email address" />
            </div>
            {prevContacts.length > 0 && <p className="text-xs text-slate-400 mt-1">Pick a previous contact above, or enter a new one.</p>}
          </div>
          <div className="sm:col-span-2"><Label>Site Address</Label>
            {prevAddresses.length > 0 && (
              <Select value={undefined} onValueChange={(v) => {
                const a = prevAddresses[Number(v)];
                if (a) setForm((f) => ({ ...f, site_address: a.address, suburb: a.suburb, state: a.state, postcode: a.postcode, latitude: a.latitude, longitude: a.longitude }));
              }}>
                <SelectTrigger data-testid="prev-address-select" className="mt-1 mb-2"><SelectValue placeholder="Use a previous site address…" /></SelectTrigger>
                <SelectContent>
                  {prevAddresses.map((a, i) => <SelectItem key={i} value={String(i)}>{a.address}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <AddressAutocomplete testid="job-address-input" value={form.site_address}
              onChange={(v) => set("site_address", v)}
              onSelect={(d) => setForm((f) => ({ ...f, site_address: d.formatted_address || d.street_address,
                suburb: d.suburb, state: d.state, postcode: d.postcode, country: d.country,
                latitude: d.latitude, longitude: d.longitude }))} />
            {form.suburb && <p className="text-xs text-slate-400 mt-1">{form.suburb}, {form.state} {form.postcode}{form.latitude ? ` · ${Number(form.latitude).toFixed(4)}, ${Number(form.longitude).toFixed(4)}` : ""}</p>}
            {prevAddresses.length > 0 && <p className="text-xs text-slate-400 mt-1">Pick a previous address above, or type a new one.</p>}
          </div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1" /></div>
          <div className="sm:col-span-2 border-t border-slate-100 pt-3">
            <Label>Billing Cycle <span className="text-red-500">*</span></Label>
            <Select value={form.billing_cycle || "weekly"} onValueChange={(v) => set("billing_cycle", v)}>
              <SelectTrigger data-testid="job-billing-cycle" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Weekly — job runs Mon–Sun; a new week starts a new job</SelectItem>
                <SelectItem value="monthly">Monthly — job runs the whole calendar month; a new month starts a new job</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400 mt-1">Controls how long this job may continue before a new Job ID is created for extra work.</p>
          </div>
          <div className="sm:col-span-2 flex items-center gap-2 border-t border-slate-100 pt-3">
            <input type="checkbox" id="multiDay" data-testid="job-multiday-toggle" checked={multiDay} onChange={(e) => toggleMultiDay(e.target.checked)} className="w-4 h-4" />
            <Label htmlFor="multiDay" className="cursor-pointer">Multi-day job — staff each day separately (e.g. week-long jobs)</Label>
          </div>
          {multiDay ? (
            <ScheduleDaysEditor days={form.schedule_days || []} onChange={(v) => set("schedule_days", v)} techs={techs} events={events} excludeJobId={job?.id} />
          ) : (
            <>
              <div>
                <Label>Assigned Technicians</Label>
                <TechMultiSelect techs={techs} value={form.technician_ids} onChange={(v) => set("technician_ids", v)} testid="job-technician-select" />
              </div>
              <div><Label>Appointment Date</Label><Input data-testid="job-date-input" type="date" value={form.appointment_date} onChange={(e) => set("appointment_date", e.target.value)} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Start</Label><Input type="time" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} className="mt-1" /></div>
                <div><Label>Finish</Label><Input type="time" value={form.finish_time} onChange={(e) => set("finish_time", e.target.value)} className="mt-1" /></div>
              </div>
            </>
          )}
          <div className="sm:col-span-2"><Label>Scope of Works</Label><Textarea data-testid="job-scope-input" value={form.scope_of_works} onChange={(e) => set("scope_of_works", e.target.value)} className="mt-1" rows={3} /></div>
          <div className="sm:col-span-2"><Label>Internal Notes</Label><Textarea value={form.internal_notes} onChange={(e) => set("internal_notes", e.target.value)} className="mt-1" rows={2} /></div>
        </div>
        {conflict && (
          <div data-testid="job-conflict-warning" className="rounded-xl border border-red-200 bg-red-50 p-3 mt-2">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-700 mt-0.5 shrink-0" />
              <div className="text-sm w-full">
                <span className="font-semibold text-red-900">Scheduling Conflict</span>
                <ul className="text-red-800 text-xs mt-1 space-y-0.5">
                  {conflict.conflicts.map((c, i) => (
                    <li key={i}>{c.worker_name} is unavailable on {c.date} ({c.type_label}{c.status === "submitted" ? ", pending" : ""}).</li>
                  ))}
                </ul>
                <p className="text-red-800 text-xs mt-2">Cancel, choose another person, change the date — or override with a reason (recorded &amp; the worker is notified).</p>
                <Input data-testid="job-override-reason" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder="Reason for override" className="mt-2 bg-white" />
                <div className="flex gap-2 justify-end mt-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setConflict(null)}>Cancel</Button>
                  <Button type="button" size="sm" data-testid="job-override-confirm" disabled={!overrideReason.trim()}
                    className="bg-red-600 hover:bg-red-700" onClick={() => save(true)}>Override &amp; Save</Button>
                </div>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="job-save-button" onClick={() => save(false)} className="bg-navy hover:bg-navy-light" disabled={!form.customer_name || (isHoliday && !holidayReason.trim())}>Save Job</Button>
        </DialogFooter>
      </DialogContent>

      <Dialog open={showRdoPopup} onOpenChange={(o) => { if (!o) setRdoAck(form.appointment_date); }}>
        <DialogContent className="max-w-md" data-testid="rdo-popup">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-orange-700"><AlertTriangle className="w-5 h-5" />Construction Industry RDO</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            <span className="font-semibold text-navy">{formatDate(form.appointment_date)}</span> is a scheduled Victorian Construction Industry RDO. Many commercial sites may be closed or have restricted access — please confirm with the Builder or Site Supervisor before scheduling work.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" data-testid="rdo-choose-another" onClick={() => { set("appointment_date", ""); setRdoAck(""); }}>Choose another date</Button>
            <Button className="bg-navy hover:bg-navy-light" data-testid="rdo-book-anyway" onClick={() => setRdoAck(form.appointment_date)}>Book anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [techs, setTechs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [initialCustomer, setInitialCustomer] = useState(null);

  useEffect(() => {
    const c = location.state?.createJobForCustomer;
    if (c) { setInitialCustomer(c); setEditing(null); setOpen(true); navigate("/jobs", { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleSetOpen = (v) => { setOpen(v); if (!v) setInitialCustomer(null); };

  // Refresh the assignable worker list whenever the booking form opens,
  // so newly approved employees/contractors appear without a full page reload.
  useEffect(() => {
    if (open) api.get("/assignable-workers").then((r) => setTechs(r.data)).catch(() => {});
  }, [open]);

  const statusParam = searchParams.get("status") || "";
  const dateParam = searchParams.get("date") || "";
  const rangeParam = searchParams.get("range") || "";
  const viewParam = searchParams.get("view") || "";
  const [filter, setFilter] = useState(["scheduled", "in_progress", "completed"].includes(statusParam) ? statusParam : "all");

  const load = () => {
    api.get("/jobs").then((r) => setJobs(r.data));
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/assignable-workers").then((r) => setTechs(r.data));
  };
  const reloadCustomers = async () => { const r = await api.get("/customers"); setCustomers(r.data); return r.data; };
  useEffect(load, []);

  const del = async (id, e) => { e?.stopPropagation(); if (!window.confirm("Delete this job?")) return; await api.delete(`/jobs/${id}`); toast.success("Job deleted"); load(); };
  const editJob = (j, e) => { e?.stopPropagation(); setEditing(j); setOpen(true); };

  const today = new Date().toISOString().slice(0, 10);
  const monday = (() => { const d = new Date(); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return d.toISOString().slice(0, 10); })();
  const sunday = (() => { const d = new Date(); d.setDate(d.getDate() + (6 - ((d.getDay() + 6) % 7))); return d.toISOString().slice(0, 10); })();

  const VIEW_LABELS = {
    awaiting_signature: "Awaiting Customer Signature", return_visits: "Return Visits",
    signed: "Signed Dockets", waiting_invoice: "Waiting for Invoice",
  };
  const STATUS_LABELS = { scheduled: "Scheduled Jobs", in_progress: "In Progress Jobs", completed: "Completed Jobs" };
  const activeFilterLabel = dateParam === "today" ? "Today's Jobs"
    : rangeParam === "week" ? "This Week"
    : viewParam ? VIEW_LABELS[viewParam]
    : statusParam ? (STATUS_LABELS[statusParam] || statusParam.replace("_", " "))
    : "";
  const clearUrlFilter = () => { setSearchParams({}); setFilter("all"); };

  const matchesUrl = (j) => {
    if (dateParam === "today" && j.appointment_date !== today) return false;
    if (rangeParam === "week" && !(j.appointment_date >= monday && j.appointment_date <= sunday)) return false;
    if (viewParam === "awaiting_signature" && j.status !== "awaiting_signature") return false;
    if (viewParam === "return_visits" && !(["return_tomorrow", "awaiting_return_visit"].includes(j.status) || (j.is_continuation && ["scheduled", "in_progress"].includes(j.status)))) return false;
    if (viewParam === "signed" && !(j.signing && j.signing.status === "signed")) return false;
    if (viewParam === "waiting_invoice" && !(j.status === "completed" && j.ready_for_invoice)) return false;
    return true;
  };

  const STATUS_ORDER = { in_progress: 0, scheduled: 1, return_tomorrow: 2, awaiting_return_visit: 2, awaiting_return: 2, awaiting_signature: 3, completed: 4 };
  const statusRank = (s) => (s in STATUS_ORDER ? STATUS_ORDER[s] : 5);
  const filtered = jobs.filter((j) => (filter === "all" || j.status === filter) && matchesUrl(j) &&
    (j.customer_name.toLowerCase().includes(q.toLowerCase()) || (j.job_number || "").toLowerCase().includes(q.toLowerCase())))
    .sort((a, b) => statusRank(a.status) - statusRank(b.status) || (b.appointment_date || "").localeCompare(a.appointment_date || ""));

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-navy">Jobs</h1>
          <p className="text-slate-500 mt-1">{jobs.length} total jobs</p>
        </div>
        <Button data-testid="new-job-button" onClick={() => { setEditing(null); setOpen(true); }} className="bg-navy hover:bg-navy-light">
          <Plus className="w-4 h-4 mr-2" /> New Job
        </Button>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="jobs-search" placeholder="Search jobs…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
        {["all", "scheduled", "in_progress", "completed"].map((s) => (
          <button key={s} data-testid={`filter-${s}`} onClick={() => setFilter(s)}
            className={`px-3 py-2 rounded-lg text-sm font-medium capitalize ${filter === s ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
            {s.replace("_", " ")}
          </button>
        ))}
      </div>

      {activeFilterLabel && (
        <div data-testid="active-filter-banner" className="flex items-center gap-2 text-sm">
          <span className="text-slate-500">Showing:</span>
          <span className="inline-flex items-center gap-2 bg-navy/5 text-navy border border-navy/10 rounded-full px-3 py-1 font-medium capitalize">
            {activeFilterLabel}
            <button data-testid="clear-filter-button" onClick={clearUrlFilter} className="hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
          </span>
        </div>
      )}

      <div className="grid gap-3">
        {filtered.map((j) => (
          <div key={j.id} data-testid={`job-row-${j.id}`} onClick={() => navigate(`/jobs/${j.id}`)}
            className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-mono text-slate-400">{j.job_number}</span>
                <StatusPill status={j.status} invoiceStatus={j.invoice_status} invoiceId={j.invoice_id} />
                {j.is_continuation && <span data-testid={`continuation-badge-${j.id}`} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1"><Link2 className="w-3 h-3" />Continuation of {j.parent_job_number}</span>}
                {j.continuation_job_number && <span data-testid={`has-continuation-badge-${j.id}`} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1"><Link2 className="w-3 h-3" />Continued → {j.continuation_job_number}</span>}
                {j.ready_for_invoice && !j.invoice_id && j.invoice_status !== "included_in_invoice" && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1"><FileClock className="w-3 h-3" />Invoice</span>}
              </div>
              <p className="font-semibold text-navy mt-1 truncate">{j.customer_name}</p>
              <p className="text-sm text-slate-500 truncate">{j.site_address}</p>
            </div>
            <div className="text-sm text-slate-500 text-right">
              <p>{formatDate(j.appointment_date)}</p>
              <p>{j.start_time} {j.technician_name && `· ${j.technician_name}`}</p>
            </div>
            <div className="flex gap-1">
              <button data-testid={`edit-job-${j.id}`} title="Quick edit" onClick={(e) => editJob(j, e)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`delete-job-${j.id}`} onClick={(e) => del(j.id, e)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-slate-400 text-center py-12">No jobs found.</p>}
      </div>

      <JobForm open={open} setOpen={handleSetOpen} job={editing} customers={customers} techs={techs} onSaved={load} reloadCustomers={reloadCustomers} initialCustomer={initialCustomer} />
    </div>
  );
}
